import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import useUpdate from "../../api/useUpdate";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";

const AccountEdit = ({ open, onClose, editData, token }) => {
  const { t } = useTranslation();
  const { handleUpdate, loading, isSuccess } = useUpdate(
    "shop/accounts",
    token
  );

  const [selectedShop, setSelectedShop] = useState("");
  const { data: currenciesData, loading: currencyLoading } = useFetchObjects(
    ["currencies", "shop-account-edit", selectedShop],
    selectedShop
      ? `shop/currencies/?page_size=all&shop=${selectedShop}`
      : "",
    token
  );

  const currencies = Array.isArray(currenciesData)
    ? currenciesData
    : currenciesData?.results || [];

  const [name, setName] = useState("");
  const [accountType, setAccountType] = useState("normal");
  const [currencyId, setCurrencyId] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (editData) {
      const sid = String(editData.shop?.id || editData.shop || "").trim();
      setSelectedShop(sid);
      setName(editData.name || "");
      setAccountType(String(editData.account_type || "normal"));
      setCurrencyId(
        String(editData.currency?.id || editData.currency || "").trim()
      );
      setNote(editData.note || "");
    }
  }, [editData]);

  useEffect(() => {
    if (isSuccess) {
      onClose();
    }
  }, [isSuccess, onClose]);

  const handleSubmit = () => {
    handleUpdate(editData.id, {
      shop: selectedShop,
      name: name.trim(),
      account_type: accountType,
      currency: currencyId,
      note: note.trim() || null,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("account.editAccount")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <TextField
            autoFocus
            margin="dense"
            label={t("account.name")}
            fullWidth
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          <TextField
            select
            margin="dense"
            label={t("account.currency")}
            fullWidth
            required
            disabled={!selectedShop || currencyLoading}
            value={currencyId}
            onChange={(e) => setCurrencyId(e.target.value)}
          >
            {!selectedShop ? (
              <MenuItem value="">{t("account.selectShopFirst")}</MenuItem>
            ) : currencyLoading ? (
              <MenuItem value="" disabled>
                <CircularProgress size={20} />
              </MenuItem>
            ) : currencies.length === 0 ? (
              <MenuItem value="" disabled>
                {t("account.noCurrencies")}
              </MenuItem>
            ) : (
              currencies.map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>
                  {c.title}
                </MenuItem>
              ))
            )}
          </TextField>

          <TextField
            margin="dense"
            label={t("account.note")}
            fullWidth
            multiline
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />

          <TextField
            select
            margin="dense"
            label={t("account.type")}
            fullWidth
            required
            value={accountType}
            onChange={(e) => setAccountType(e.target.value)}
          >
            <MenuItem value="normal">{t("account.normal", "Normal")}</MenuItem>
            <MenuItem value="sarap">{t("account.sarap", "Sarap")}</MenuItem>
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("account.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={
            loading || !selectedShop || !name.trim() || !currencyId || !accountType
          }
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("account.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AccountEdit;
